import { useState, useMemo } from 'react';
import _ from 'lodash';
import { t, Trans } from '@lingui/macro';
import { Box, Button } from '@material-ui/core';
import { FormProvider, useForm } from 'react-hook-form';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import NgpForm from '../../form/NgpForm';
import NgpTagWarningField from '../../form/NgpTagWarningField';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { NgpTagsField } from '../../form/NgpTagsField';
import { AGENT_BULK_EDIT_TAGS_FIELD, AGENT_TAGS_FIELD } from './AgentConstants';
import { useTags } from '../../../services/FormQueryService';
import { useDataService } from '../../../services/ApiDataService';
import {
    NAME_FIELD,
    TAGS_FIELD,
    TEST_STATUS_FIELD,
    TEST_STATUS_RUNNING,
} from '../service-tests/ServiceTestAdminConstants';

const formId = 'agentTagsForm';
const apiTagsUrl = '/ipm/v1/open/agents/tags';
const defaultValues = { [AGENT_BULK_EDIT_TAGS_FIELD]: [] };

export default function AgentListBulkEdit({
    onConfirm,
    disabled = false,
    tableSelection,
    tableSearch = '',
    searchFields,
}) {
    const dataService = useDataService();
    const methods = useForm({
        ...USE_FORM_DEFAULT_OPTIONS,
        defaultValues,
    });
    const { handleSubmit, reset } = methods;
    const [showBulkEditDialog, setShowBulkEditDialog] = useState(false);
    const [confirmDisabled, setConfirmDisabled] = useState(true);
    const { tags: agentTags } = useTags({ url: apiTagsUrl });
    const [loading, setLoading] = useState(false);
    const [agentTestDataList, setAgentTestDataList] = useState([]);
    const handleAgentTagging = () => {
        setShowBulkEditDialog(true);
        setConfirmDisabled(true);
    };
    const MAX_AGENT_PER_PLM = 5000;

    const handleFormSubmit = (dataToSave) => {
        onConfirm({
            [AGENT_BULK_EDIT_TAGS_FIELD]: _.flatten(dataToSave[AGENT_BULK_EDIT_TAGS_FIELD]),
        });
        handleCleanup();
    };

    useMemo(() => {
        if (!_.isEmpty(tableSelection)) {
            const agentUrl = `/query/admin/agents/findSelected`;

            setLoading(true);
            dataService
                .post(agentUrl, tableSelection, { search: tableSearch, limit: MAX_AGENT_PER_PLM, searchFields }) // Set the limit to the max number of agents allowed on a plm in case a user does a select all and tags
                .then((response) => {
                    return _.filter(response.data, (agent) => agent.type === 'G');
                })
                .then((selectedGlobalAgents) => {
                    const promiseList = [];

                    _.forEach(selectedGlobalAgents, (selectedGlobalAgent) => {
                        const agentTestsUrl = `/ipm/v1/admin/agents/${selectedGlobalAgent._id}/testsSearchable`;
                        const agentTestsParams = {
                            sort: NAME_FIELD,
                            searchFields: [NAME_FIELD, TAGS_FIELD],
                            tags: selectedGlobalAgent.tags || [],
                        };
                        promiseList.push(
                            dataService
                                .get(agentTestsUrl, agentTestsParams)
                                .then((tests) =>
                                    _.filter(tests, (test) => test[TEST_STATUS_FIELD] === TEST_STATUS_RUNNING)
                                )
                                .then((tests) => {
                                    return {
                                        agent: selectedGlobalAgent,
                                        tests,
                                    };
                                })
                        );
                    });

                    Promise.all(promiseList).then((agentTestData) => {
                        setAgentTestDataList(agentTestData);
                        setLoading(false);
                    });
                });
        }
    }, [tableSelection, tableSearch, searchFields, dataService]);

    const handleCleanup = () => {
        reset(defaultValues);
        setShowBulkEditDialog(false);
    };

    const onChangeTags = (tags) => {
        const tagLen = tags?.length || 0;
        if (_.isArray(tags)) {
            setConfirmDisabled(tagLen === 0);
        } else {
            setConfirmDisabled(tagLen === 0 || tags.trim().length === 0);
        }
    };

    return (
        <>
            <Button onClick={handleAgentTagging} disabled={disabled}>
                <Trans>Tag</Trans>
            </Button>
            <NgpTwoButtonDialog
                title={<Trans>Tag nPoints</Trans>}
                confirmButtonText={<Trans>Save</Trans>}
                cancelButtonText={<Trans>Cancel</Trans>}
                confirmButtonDisabled={confirmDisabled}
                onCloseFunc={handleCleanup}
                open={showBulkEditDialog}
                setOpen={setShowBulkEditDialog}
                onConfirm={handleSubmit(handleFormSubmit)}
                disableBackdropClick
            >
                <Box width={600}>
                    <FormProvider {...methods}>
                        <NgpForm
                            formId={formId}
                            initialized
                            loading={false}
                            showSave={false}
                            formPadding={false}
                            showRequiredFieldsTip={false}
                            submitFunction={handleFormSubmit}
                        >
                            <NgpTagsField
                                fieldName={AGENT_BULK_EDIT_TAGS_FIELD}
                                label={t`Tags`}
                                options={agentTags}
                                onChangeFunc={onChangeTags}
                                required
                            />

                            {!_.isEmpty(agentTestDataList) && !loading && (
                                <NgpTagWarningField
                                    fieldName={AGENT_TAGS_FIELD}
                                    agentTestDataList={agentTestDataList}
                                />
                            )}
                        </NgpForm>
                    </FormProvider>
                </Box>
            </NgpTwoButtonDialog>
        </>
    );
}
